import React, { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { Helmet } from 'react-helmet';
import { z } from 'zod';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { IconSearchSvg } from 'apps/sigma2-ui/src/components/svg/IconSearch';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { DatabaseRevision } from 'libs/end-to-end/src/types/databases/revisions/database-revision';
import { TransformDictionaryRow } from 'libs/end-to-end/src/types/databases/transform-dictionary-row';

const DictionaryResponse = z.object({
  rows: z.array(TransformDictionaryRow),
});

// `metadata` is a JSON-encoded object in the source row with flexible keys
// (display_name, user_facing, best, computation_method, enum_values, etc.).
// The page consumes a small typed subset: `display_name` for the card header,
// `example` for the inline Example field, and `examples` as the presence gate
// for the "View Examples" button. Unknown keys pass through untyped.
const DictionaryMetadata = z
  .object({
    display_name: z.string().optional(),
    example: z.unknown().optional(),
    examples: z.unknown().optional(),
  })
  .partial()
  .passthrough();
type DictionaryMetadata = z.infer<typeof DictionaryMetadata>;

const parseDictionaryMetadata = (
  raw: string | null | undefined,
): DictionaryMetadata => {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return {};
  }
  try {
    const result = DictionaryMetadata.safeParse(JSON.parse(raw));
    return result.success ? result.data : {};
  } catch {
    return {};
  }
};

// A row paired with its parsed `metadata` blob and a precomputed lowercase
// search string. Parsing + haystack assembly happen once at the page level
// (see DataCatalogPage) so keystroke-level filtering is a single `.includes()`
// per entry — no per-keystroke join/lowercase on potentially large catalogs.
interface DictionaryEntry {
  row: TransformDictionaryRow;
  metadata: DictionaryMetadata;
  searchHaystack: string;
}

const toDictionaryEntry = (row: TransformDictionaryRow): DictionaryEntry => {
  const metadata = parseDictionaryMetadata(row.metadata);
  const searchHaystack = [
    metadata.display_name,
    row.column_name,
    row.description,
    row.computation_method,
    row.source_generator,
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
  return { row, metadata, searchHaystack };
};

// Pretty label for a column. Falls back to the raw `column_name` so the
// header is never empty when `metadata.display_name` is absent.
const resolveDisplayName = (entry: DictionaryEntry): string => {
  const fromMetadata = entry.metadata.display_name?.trim();
  return fromMetadata && fromMetadata.length > 0
    ? fromMetadata
    : entry.row.column_name;
};

// `enum_values` is a JSON-encoded array string (e.g. `'["Yes","No"]'`).
// Render as a comma-joined list when parseable; fall back to the raw string
// so an unexpected payload is still visible rather than silently dropped.
const formatEnumValues = (
  raw: string | null | undefined,
): string | undefined => {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((entry) => String(entry)).join(', ');
    }
  } catch {
    // fall through to raw
  }
  return raw;
};

export const DataCatalogPage: React.FC = () => {
  useAuthenticatedPageRedirect();
  useObservation();
  useLanguage();

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  // The catalog is scoped to the user's currently-selected revision's transform.
  // Other revision-scoped pages (Overview, Copilot, Explore) use the same
  // selection hooks — same convention here.
  const orgId = useSelectedOrganizationId();
  const databaseId = useSelectedDatabaseId();
  const revisionId = useSelectedDatabaseRevisionId();

  const getRevisionUrlFn = useCallback(
    () =>
      orgId && databaseId && revisionId
        ? `${getApiBaseUrl()}/organizations/${orgId}/databases/${databaseId}/revisions/${revisionId}`
        : null,
    [orgId, databaseId, revisionId],
  );
  const revisionReadPattern = useSimpleReadPattern({
    getUrlFn: getRevisionUrlFn,
    canRead: !!orgId && !!databaseId && !!revisionId,
    responseSchema: DatabaseRevision,
  });

  const transformId = useMemo<string | null>(() => {
    if (revisionReadPattern.status !== 'done' || !revisionReadPattern.entity) {
      return null;
    }
    return revisionReadPattern.entity.transformId ?? null;
  }, [revisionReadPattern]);

  const getDictionaryUrlFn = useCallback(
    () =>
      orgId && databaseId && transformId
        ? `${getApiBaseUrl()}/organizations/${orgId}/databases/${databaseId}/transforms/${transformId}/dictionary`
        : null,
    [orgId, databaseId, transformId],
  );
  const dictionaryReadPattern = useSimpleReadPattern({
    getUrlFn: getDictionaryUrlFn,
    canRead: !!orgId && !!databaseId && !!transformId,
    responseSchema: DictionaryResponse,
  });

  const dictionaryRows = useMemo(
    () =>
      dictionaryReadPattern.status === 'done' && dictionaryReadPattern.entity
        ? dictionaryReadPattern.entity.rows
        : [],
    [dictionaryReadPattern],
  );

  // Parse `metadata` JSON once per row here at the page level. Downstream
  // card components read straight from the already-parsed object instead of
  // re-parsing in both the header and the details panel.
  const entries = useMemo(
    () => dictionaryRows.map(toDictionaryEntry),
    [dictionaryRows],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedColumnName, setExpandedColumnName] = useState<string | null>(
    null,
  );

  const filteredEntries = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (needle === '') {
      return entries;
    }
    return entries.filter((entry) => entry.searchHaystack.includes(needle));
  }, [entries, searchTerm]);

  // Stable toggle handler keeps memoized cards from re-rendering on every
  // keystroke. Without this the inline lambda would be a new reference each
  // render and defeat React.memo on DataCatalogCard.
  const handleToggle = useCallback((columnName: string) => {
    setExpandedColumnName((prev) => (prev === columnName ? null : columnName));
  }, []);

  const isLoadingDictionary =
    revisionReadPattern.status === 'loading' ||
    dictionaryReadPattern.status === 'loading';

  // When we know the user has no revision context we can't hit the endpoint
  // at all. Distinguish that from "revision known but transform missing" for
  // a clearer empty-state message.
  const hasRevisionContext = !!orgId && !!databaseId && !!revisionId;
  const revisionHasTransform =
    revisionReadPattern.status === 'done' && !!transformId;

  return (
    <MainLayout showBackToTop={true}>
      <Helmet>
        <title>Data Dictionary</title>
      </Helmet>

      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isLoadingStill}
        failedToLoadObservation={failedToLoadObservation}
      />

      {!isLoadingStill && (
        <div className="bg-gray-100 min-h-full w-full px-6 py-10">
          <div className="max-w-[1160px] mx-auto flex flex-col gap-10">
            <DataCatalogHeader
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
            />

            <DataCatalogList
              entries={filteredEntries}
              expandedColumnName={expandedColumnName}
              onToggle={handleToggle}
              isLoading={isLoadingDictionary}
              hasRawData={entries.length > 0}
              hasRevisionContext={hasRevisionContext}
              revisionHasTransform={revisionHasTransform}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

const DataCatalogHeader: React.FC<{
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
}> = ({ searchTerm, onSearchTermChange }) => {
  return (
    <div className="flex flex-col items-center gap-7">
      <div className="flex flex-col items-center gap-5 w-full">
        <S2Typography
          variant="headline-2"
          textColorVariant="gray-1000"
          translatedContent="Data Dictionary"
        />
        <SearchField value={searchTerm} onChange={onSearchTermChange} />
      </div>
    </div>
  );
};

const SearchField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  return (
    <div className="bg-white border-1 border-gray-300 rounded flex items-center gap-4 h-10 px-4 w-full max-w-[840px]">
      <span className="text-gray-600 shrink-0 w-5 h-5 flex items-center justify-center">
        <IconSearchSvg
          svg={{ width: 20, height: 20, viewBox: '0 0 24 24' }}
          wantsCurrentColor
        />
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search a term"
        className="flex-1 min-w-0 bg-transparent border-0 outline-none font-sans text-xs text-gray-1000 placeholder:text-gray-600 focus:ring-0"
        data-qa="data-catalog-search"
      />
    </div>
  );
};

const DataCatalogList: React.FC<{
  entries: DictionaryEntry[];
  expandedColumnName: string | null;
  onToggle: (columnName: string) => void;
  isLoading: boolean;
  hasRawData: boolean;
  hasRevisionContext: boolean;
  revisionHasTransform: boolean;
}> = ({
  entries,
  expandedColumnName,
  onToggle,
  isLoading,
  hasRawData,
  hasRevisionContext,
  revisionHasTransform,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[200px]">
        <CustomVizCircularSpinner />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center py-10">
        <S2Typography
          variant="body-6"
          textColorVariant="gray-700"
          translatedContent={resolveEmptyStateMessage({
            hasRawData,
            hasRevisionContext,
            revisionHasTransform,
          })}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      {entries.map((entry) => (
        <DataCatalogCard
          key={entry.row.column_name}
          entry={entry}
          isExpanded={expandedColumnName === entry.row.column_name}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
};

const resolveEmptyStateMessage = ({
  hasRawData,
  hasRevisionContext,
  revisionHasTransform,
}: {
  hasRawData: boolean;
  hasRevisionContext: boolean;
  revisionHasTransform: boolean;
}): string => {
  if (!hasRevisionContext) {
    return 'Select a revision to see its data dictionary.';
  }
  if (!revisionHasTransform) {
    return 'This revision has no published transform, so no data dictionary is available.';
  }
  if (hasRawData) {
    return 'No terms match your search.';
  }
  return 'No data dictionary is available for this revision.';
};

// Memoized so that typing in the search box (which re-renders the page) only
// re-renders cards whose `isExpanded` actually flips. Requires a stable
// `onToggle` reference from the parent (see `handleToggle`).
const DataCatalogCard = React.memo<{
  entry: DictionaryEntry;
  isExpanded: boolean;
  onToggle: (columnName: string) => void;
}>(({ entry, isExpanded, onToggle }) => {
  const { row } = entry;
  const displayName = resolveDisplayName(entry);
  return (
    <div className="bg-white rounded-lg shadow-12px w-full overflow-hidden">
      {/* Top highlight bar — appears when the card is expanded. Animates
          height + opacity together so it visually slides in with the
          details panel rather than flashing on. */}
      <div
        className={cn(
          'bg-indigo-900 transition-all duration-300 ease-in-out',
          isExpanded ? 'h-1 opacity-100' : 'h-0 opacity-0',
        )}
      />
      <button
        type="button"
        onClick={() => onToggle(row.column_name)}
        aria-expanded={isExpanded}
        className="flex gap-12 items-center pl-6 pr-4 py-5 w-full text-left"
      >
        <div className="flex items-center shrink-0 w-56">
          <S2Typography
            variant="body-6-bold"
            textColorVariant="gray-1000"
            truncate
            translatedContent={displayName}
          />
        </div>
        <div className="flex flex-1 min-w-0 items-center">
          <S2Typography
            variant="body-7-medium"
            textColorVariant="gray-700"
            truncate
            translatedContent={row.description ?? ''}
          />
        </div>
        {row.semantic_type && row.semantic_type.trim().length > 0 && (
          <div className="shrink-0 rounded-full bg-gray-100 px-3 py-1 max-w-[200px]">
            <S2Typography
              variant="body-7-medium"
              textColorVariant="gray-700"
              truncate
              translatedContent={row.semantic_type}
            />
          </div>
        )}
        <div className="flex items-center shrink-0 text-gray-700">
          <span
            className={cn(
              'w-6 h-6 flex items-center justify-center transition-transform',
              isExpanded ? 'rotate-90' : '-rotate-90',
            )}
          >
            <IconChevronRightSvg
              svg={{ width: 24, height: 24 }}
              wantsCurrentColor
            />
          </span>
        </div>
      </button>

      {/* Animated expand/collapse via grid-template-rows 0fr → 1fr.
          Content is always mounted so the transition has a target height
          to animate to; overflow-hidden on the inner wrapper clips it when
          collapsed. aria-hidden mirrors the collapsed state for AT users. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!isExpanded}
      >
        <div className="overflow-hidden">
          <DataCatalogCardDetails entry={entry} />
        </div>
      </div>
    </div>
  );
});
DataCatalogCard.displayName = 'DataCatalogCard';

// Extracted so the component body stays under the cyclomatic-complexity
// threshold — inlining the 5-way `||` chain pushed it over.
const hasAnyDetailContent = (
  row: TransformDictionaryRow,
  metadata: DictionaryMetadata,
  possibleValuesDisplay: string | undefined,
  exampleValue: string | undefined,
): boolean =>
  !!row.computation_method?.trim() ||
  !!possibleValuesDisplay?.trim() ||
  !!exampleValue?.trim() ||
  !!row.semantic_type?.trim() ||
  metadata.examples !== undefined;

const DataCatalogCardDetails: React.FC<{
  entry: DictionaryEntry;
}> = ({ entry }) => {
  // Metadata is parsed once per page load (see DataCatalogPage); consume the
  // already-parsed object here so the expanded panel doesn't re-parse on
  // every render of the card.
  const { row, metadata } = entry;

  const possibleValuesDisplay = useMemo(
    () => formatEnumValues(row.enum_values),
    [row.enum_values],
  );
  const exampleValue =
    typeof metadata.example === 'string' ? metadata.example : undefined;

  // If none of the detail slots have visible content — including the
  // "View Examples" button — we show a single "Specifications unavailable"
  // placeholder instead of a visually empty expanded area.
  const hasAnyContent = hasAnyDetailContent(
    row,
    metadata,
    possibleValuesDisplay,
    exampleValue,
  );

  return (
    <div className="flex flex-col gap-6 px-6 pb-5">
      <div className="h-px w-full bg-gray-200" />

      <div className="flex flex-col gap-6 pl-[272px]">
        {!hasAnyContent ? (
          <S2Typography
            variant="body-7-medium"
            textColorVariant="gray-600"
            translatedContent="Specifications unavailable"
          />
        ) : (
          <>
            <div className="flex gap-12 flex-wrap">
              <DetailField label="Reference Name" value={row.column_name} />
              <DetailField
                label="Computation"
                value={row.computation_method}
                fillRow
              />
            </div>
            <div className="flex gap-12 flex-wrap">
              <DetailField
                label="Possible Values"
                value={possibleValuesDisplay}
              />
              <DetailField label="Example" value={exampleValue} />
            </div>

            {metadata.examples !== undefined && (
              <div className="flex gap-12 items-end flex-wrap">
                <div className="flex flex-1 min-w-[120px] justify-end">
                  {/* TODO: "View Examples" link target is unresolved.
                      Disabled until routed, but the button only shows
                      when the source metadata actually has an `examples`
                      payload. */}
                  <button
                    type="button"
                    disabled
                    className="bg-transparent border-0 p-0 cursor-not-allowed opacity-60"
                  >
                    <S2Typography
                      variant="body-6-bold"
                      textColorVariant="indigo-900"
                      translatedContent="View Examples"
                    />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DetailField: React.FC<{
  label: string;
  value: string | undefined;
  // Fills the remaining space in the parent flex row instead of taking the
  // default 180px column. Use for long values (e.g. Computation) that need
  // room to breathe alongside narrower siblings on the same row.
  fillRow?: boolean;
}> = ({ label, value, fillRow = false }) => {
  // Render nothing when the value is absent or empty — the label should
  // disappear alongside its value rather than showing an orphan em-dash.
  if (!value || value.trim().length === 0) {
    return null;
  }
  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        fillRow ? 'flex-1 min-w-0' : 'w-[180px]',
      )}
    >
      <S2Typography
        variant="body-7-bold"
        textColorVariant="gray-1000"
        truncate
        translatedContent={label}
      />
      {/* Value wraps instead of truncating — long descriptions and JSON
          blobs stay fully readable. break-words handles pathological
          single-token strings (e.g. snake_case identifiers longer than
          the column width). */}
      <S2Typography
        variant="body-7-medium"
        textColorVariant="gray-700"
        translatedContent={value}
        dangerouslyOverrideClassNameFn={(cn) =>
          `${cn} whitespace-normal break-words`
        }
      />
    </div>
  );
};
